import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";

import { ButtonIcon } from "@orgmym/leeloo-core";
import { useTranslation } from "react-i18next";

import { MAX_MESSAGE_LENGTH } from "@/shared/constants/messages";
import * as styles from "./message-input.css";

export interface MessageInputProps
  extends Omit<React.ComponentPropsWithRef<"textarea">, "value"> {
  value?: string;
  isLoading?: boolean;
  onSend?: (content: string) => void;
  onFocusChange?: (focused: boolean) => void;
}

export interface MessageInputHandle {
  focus: () => void;
  clear: () => void;
  getValue: () => string;
}

export const MessageInput = React.forwardRef<
  MessageInputHandle,
  MessageInputProps
>(
  (
    {
      className,
      disabled = false,
      isLoading = false,
      placeholder: placeholderProp,
      value: controlledValue,
      onSend,
      onChange,
      onKeyDown,
      onFocus,
      onBlur,
      onFocusChange,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const placeholder =
      placeholderProp ?? t("creators_messaging_chat_write_placeholder");

    const [internalValue, setInternalValue] = useState("");
    const [focused, setFocused] = useState(false);
    const [isMultiline, setIsMultiline] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const preventBlurRef = useRef(false);

    const isControlled = controlledValue !== undefined;
    const value = isControlled ? controlledValue : internalValue;

    useImperativeHandle(
      ref,
      () => ({
        focus: () => textareaRef.current?.focus(),
        clear: () => {
          if (!isControlled) {
            setInternalValue("");
          }
          if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
          }
          setIsMultiline(false);
        },
        getValue: () =>
          typeof value === "string" ? value : String(value || "")
      }),
      [isControlled, value]
    );

    useEffect(() => {
      onFocusChange?.(focused);
    }, [focused, onFocusChange]);

    const previousIsLoadingRef = useRef(isLoading);
    useEffect(() => {
      if (previousIsLoadingRef.current && !isLoading) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (
              textareaRef.current &&
              document.activeElement !== textareaRef.current
            ) {
              textareaRef.current.focus();
            }
          }, 50);
        });
      }
      previousIsLoadingRef.current = isLoading;
    }, [isLoading]);

    const handleSend = useCallback(() => {
      const stringValue =
        typeof value === "string" ? value : String(value || "");
      const trimmedValue = stringValue.trim();
      if (!trimmedValue || isLoading || disabled || !onSend) return;

      preventBlurRef.current = true;
      setTimeout(() => {
        preventBlurRef.current = false;
      }, 200);

      onSend(trimmedValue);
      if (!isControlled) {
        setInternalValue("");
      }
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
      setIsMultiline(false);
    }, [value, onSend, isLoading, disabled, isControlled]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleSend();
        }
        onKeyDown?.(e);
      },
      [handleSend, onKeyDown]
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setFocused(true);
        onFocus?.(e);
      },
      [onFocus]
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLTextAreaElement>) => {
        if (preventBlurRef.current && textareaRef.current) {
          e.preventDefault();
          textareaRef.current.focus();
          return;
        }
        setFocused(false);
        onBlur?.(e);
      },
      [onBlur]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (!isControlled) {
          setInternalValue(e.target.value);
        }
        onChange?.(e);
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
          const scrollHeight = textareaRef.current.scrollHeight;
          textareaRef.current.style.height = `${scrollHeight}px`;
          setIsMultiline(scrollHeight > 28);
        }
      },
      [onChange, isControlled]
    );

    const charCount = typeof value === "string" ? value.length : 0;
    const isOverLimit = charCount > MAX_MESSAGE_LENGTH;

    const canSendMessages = useMemo(
      () =>
        typeof value === "string" &&
        value.trim().length > 0 &&
        !isOverLimit &&
        !isLoading &&
        !disabled,
      [value, isLoading, disabled, isOverLimit]
    );

    return (
      <div className={styles.inputContainer}>
        {isOverLimit && (
          <span
            className={styles.charCounter}
            data-testid="message-char-counter"
          >
            {charCount}/{MAX_MESSAGE_LENGTH}
          </span>
        )}
        <div
          className={styles.inputWrapper({ focused, multiline: isMultiline })}
        >
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            className={styles.textarea}
            rows={1}
            id={props.id || "message-input"}
            name={props.name || "message-input"}
            aria-label="Message input"
            data-testid="message-input"
            {...props}
          />
          {onSend && (
            <ButtonIcon
              iconName={isLoading ? "loading" : "send-message"}
              iconSize="medium"
              variant="simple"
              size="medium"
              onPress={handleSend}
              className={styles.sendButton({ hasText: canSendMessages })}
              disabled={!canSendMessages}
              aria-label="Send message"
              data-testid="message-send-button"
            />
          )}
        </div>
      </div>
    );
  }
);

MessageInput.displayName = "MessageInput";

export default MessageInput;
